import { getUserContainer } from '../../container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Modal,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AppTable, Field } from '../../components/table'
import { fromModel, User, UserQuery } from '../../modules/users/models/User'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { BoolQueryParam, Query, QueryParam, QueryParamN } from '../../common/api/Query'
import {
  ROUTE_DASHBOARD,
  ROUTE_USER_DETAIL,
  ROUTE_USERS_CREATE,
} from '../../routes/routes-constants'
import { navigate } from '@reach/router'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { CustomModal } from '../../components/modal/CustomModal'
import { useSnackbar } from 'notistack'
import { ModalFamiliar } from './ModalFamiliar'
import { useUserRoles } from '../../modules/users/hooks/useUserRoles'
import { PatientFamily, PatientFamilyDTO } from '../../modules/users/models/PatientFamily'
import { PageLayout } from 'pages/layout'
import { forkJoin, of } from 'rxjs'

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

const ROWS_PER_PAGE = 5

// The backend stores no sensitivity yet, so every user renders as "normal".
// Once the field exists, sensitivityOf() is the only place to change.
const SENSITIVITY = {
  normal: { color: '#f1b37a', label: 'normal' },
  sensitive: { color: '#8862c5', label: 'sensitive' },
} as const

type SensitivityKey = keyof typeof SENSITIVITY

const sensitivityOf = (u: User): SensitivityKey => (u.sensitive ? 'sensitive' : 'normal')

// Colour dot with the design legend on hover.
function SensitivityDot({ value }: { value: SensitivityKey }) {
  const { t } = useTranslation()

  return (
    <Tooltip
      arrow
      title={
        <Box>
          <Typography sx={{ fontSize: '12px', mb: 0.5 }}>{t('protocolSensitivity')}</Typography>
          {(Object.keys(SENSITIVITY) as SensitivityKey[]).map((k) => (
            <Box key={k} display="flex" alignItems="center" gap={0.75}>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: SENSITIVITY[k].color,
                }}
              />
              <Typography sx={{ fontSize: '12px' }}>{t(SENSITIVITY[k].label)}</Typography>
            </Box>
          ))}
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          flexShrink: 0,
          cursor: 'default',
          backgroundColor: SENSITIVITY[value].color,
        }}
      />
    </Tooltip>
  )
}

// The backend only stores isActive, so there are just two states.
function StatusBadge({ active }: { active: boolean }) {
  const { t } = useTranslation()
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 2,
        py: 0.5,
        borderRadius: '12px',
        fontSize: '13px',
        backgroundColor: active ? 'rgba(88, 169, 66, 0.16)' : 'var(--gray-soft)',
        color: active ? 'var(--success)' : 'var(--gray-text)',
      }}
    >
      {t(active ? 'active' : 'inactive')}
    </Box>
  )
}

export function Table() {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [users, setUsers] = useState<User[]>([])
  const [count, setCount] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const [search, setSearch] = useState<string>('')
  const [onlyActive, setOnlyActive] = useState<boolean | null>(null)
  const [relatives, setRelatives] = useState<Record<string, number>>({})
  const [selected, setSelected] = useState<string[]>([])

  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)
  const [userToToggle, setUserToToggle] = useState<User | undefined>()
  const { enqueueSnackbar } = useSnackbar()
  const [selectedPatient, setSelectedPatient] = useState<User | null>(null)
  const [loggedUserData, setLoggedUserData] = useState<User | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)
  const [patientIds, setPatientIds] = useState<string[]>([])

  const loggedUser = loggedUserService.get()
  const {
    isFamiliar: isLoggedUserFamiliar,
    canCreatePatient,
    canEditPatientData,
    canManagePatientCircle,
  } = useUserRoles(loggedUser)

  useEffect(() => {
    const lu = loggedUserService.get()
    if (lu?.id) {
      userService.getByID(lu.id).subscribe((userData) => {
        if (userData) setLoggedUserData(userData)
      })
    }
  }, [])

  useEffect(() => {
    if (!loggedUserData || !isLoggedUserFamiliar) return
    const familiarId = loggedUserService.get()?.id
    if (!familiarId) return
    userService.getPatientsByFamiliar(familiarId).subscribe((families: PatientFamilyDTO[]) => {
      setPatientIds(families.map((f) => f.patientID))
    })
  }, [loggedUserData, isLoggedUserFamiliar])

  useEffect(() => {
    if (!loggedUserData) return
    setIsLoading(true)

    const queryParams: (QueryParam<UserQuery> | QueryParamN<UserQuery>)[] = [
      { name: 'isPatient', value: new BoolQueryParam(true) },
    ]
    if (search.trim()) queryParams.push({ name: 'firstName', value: search.trim() })

    if (isLoggedUserFamiliar) {
      if (patientIds.length > 0) {
        queryParams.push({ name: 'ids', value: patientIds })
      } else {
        setUsers([])
        setCount(0)
        setIsLoading(false)
        return
      }
    } else {
    }

    userService
      .getFilteredList(
        new Query({
          query: queryParams,
          pager: { offset: page * ROWS_PER_PAGE, limit: ROWS_PER_PAGE },
        })
      )
      .subscribe((res) => {
        setUsers(res.items)
        setCount(res.count)
        setIsLoading(false)
      })
  }, [loggedUserData, search, page, isLoggedUserFamiliar, patientIds, refreshTrigger])

  // The relative count is fetched only for the five visible rows.
  useEffect(() => {
    if (!users.length) return
    forkJoin(
      users.map((u) => (u.id ? userService.getFamilies(u.id) : of([] as PatientFamily[])))
    ).subscribe((res) => {
      const map: Record<string, number> = {}
      users.forEach((u, i) => (map[u.id] = (res[i] || []).length))
      setRelatives(map)
    })
  }, [users])

  const visible = onlyActive === null ? users : users.filter((u) => u.isActive === onlyActive)

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const actionIcon = (
    Icon: React.ElementType,
    label: string,
    handler: () => void,
    opts: { hidden?: boolean; red?: boolean } = {}
  ) =>
    opts.hidden ? null : (
      <Tooltip title={t(label)}>
        <IconButton
          size="small"
          onClick={handler}
          sx={{ p: 0.5, color: opts.red ? 'var(--destructive, #e14c54)' : '#9b9d9a' }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    )

  // Asks for confirmation before applying the change.
  const toggleActive = (u: User) => {
    const changed = new User(u)
    changed.isActive = !u.isActive
    userService.update(fromModel(changed)).subscribe(() => {
      setUserToToggle(undefined)
      enqueueSnackbar(t(u.isActive ? 'userDeactivated' : 'userActivated'), { variant: 'success' })
      setRefreshTrigger((p) => p + 1)
    })
  }

  const fields: Field<User>[] = [
    {
      // The checkbox shares the name cell: in its own column AppTable would
      // give it an equal share of the width.
      name: 'firstName',
      label: t('userName'),
      renderFunc: (f, u) => (
        <Box display="flex" alignItems="center" gap={1.5}>
          <Checkbox
            size="small"
            checked={selected.includes(u.id)}
            onChange={() => toggle(u.id)}
            sx={{ p: 0, color: 'var(--gray-border)' }}
          />
          <SensitivityDot value={sensitivityOf(u)} />
          <Typography
            onClick={() => navigate(ROUTE_USER_DETAIL.replace(':id', u.id))}
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--gray-medium)',
              cursor: 'pointer',
            }}
            title={t('userProfile')}
          >
            {`${u.firstName} ${u.lastName}`}
          </Typography>
        </Box>
      ),
    },
    {
      // No data: neither the model nor the backend stores the last login.
      name: 'email',
      label: t('lastLogin'),
      renderFunc: () => (
        <Typography sx={{ fontSize: '14px', color: 'var(--gray-border)' }}>—</Typography>
      ),
    },
    {
      name: 'isActive',
      label: t('status'),
      renderFunc: (f, u) => <StatusBadge active={u.isActive} />,
    },
    {
      name: 'id',
      label: t('relatives'),
      // The relative count is the only entry point left to the relatives modal.
      renderFunc: (f, u) => {
        const relativeCount = relatives[u.id]
        const canManageRelatives = canManagePatientCircle && !isLoggedUserFamiliar
        // Underlined when clickable: nothing else hints that it opens a modal.
        return (
          <Typography
            onClick={() => canManageRelatives && setSelectedPatient(u)}
            sx={{
              fontSize: '14px',
              cursor: canManageRelatives ? 'pointer' : 'default',
              textDecoration: canManageRelatives ? 'underline' : 'none',
            }}
            title={canManageRelatives ? t('addFamiliar') : undefined}
          >
            {relativeCount ?? '—'}
          </Typography>
        )
      },
    },
    {
      name: 'isPatient',
      label: t('actions'),
      renderFunc: (f, u) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          {actionIcon(VisibilityOutlinedIcon, 'see', () =>
            navigate(ROUTE_USER_DETAIL.replace(':id', u.id))
          )}
          {actionIcon(InsertChartOutlinedIcon, 'statistics', () =>
            navigate(`${ROUTE_DASHBOARD}/${u.id}`)
          )}
          {actionIcon(
            u.isActive ? RemoveCircleOutlineIcon : InfoOutlinedIcon,
            u.isActive ? 'deactivate' : 'activate',
            () => setUserToToggle(u),
            { hidden: !canEditPatientData || isLoggedUserFamiliar }
          )}
        </Box>
      ),
    },
  ]

  return (
    <PageLayout>
      <Modal open={Boolean(userToToggle)} onClose={() => setUserToToggle(undefined)}>
        <CustomModal
          title={t(userToToggle?.isActive ? 'deactivateUserTitle' : 'activateUserTitle')}
          warningText={t(userToToggle?.isActive ? 'deactivateUserWarning' : 'activateUserWarning')}
          handleClose={() => setUserToToggle(undefined)}
          handleSave={() => userToToggle && toggleActive(userToToggle)}
        />
      </Modal>

      {/* width al 100%: .innerCard de PageLayout centra a los hijos que no lo declaran */}
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        width="100%"
        mb={3}
        gap={2}
      >
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 600, color: 'var(--gray-medium)' }}>
            {t('usersTitle')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'var(--gray-text)' }}>
            {t('manageUsers')}
          </Typography>
        </Box>

        {canCreatePatient && !isLoggedUserFamiliar && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(ROUTE_USERS_CREATE)}
            sx={{
              textTransform: 'none',
              borderRadius: 'var(--button-radius)',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              backgroundColor: 'var(--green-primary)',
              '&:hover': { backgroundColor: 'var(--green-dark)', boxShadow: 'none' },
            }}
          >
            {t('newUser')}
          </Button>
        )}
      </Box>

      <Box display="flex" alignItems="center" width="100%" gap={1.5} mb={2}>
        <TextField
          size="small"
          placeholder={t('searchUser')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(0)
          }}
          sx={{ flexGrow: 1, maxWidth: '420px', '& fieldset': { borderRadius: '6px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'var(--gray-border)' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{
            textTransform: 'none',
            borderRadius: 'var(--button-radius)',
            borderColor: 'var(--gray-border)',
            color: 'var(--gray-text)',
            '&:hover': { borderColor: 'var(--green-primary)', backgroundColor: 'transparent' },
          }}
        >
          {t('filters')}
        </Button>
      </Box>

      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
      >
        {[
          { v: null, label: t('all') },
          { v: true, label: t('active') },
          { v: false, label: t('inactive') },
        ].map((o) => (
          <MenuItem
            key={String(o.v)}
            selected={onlyActive === o.v}
            onClick={() => {
              setOnlyActive(o.v)
              setFilterAnchor(null)
            }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>

      <AppTable rowKeyField="id" items={visible} fields={fields} isLoading={isLoading} />

      {!isLoading && visible.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">{t('noResults')}</Typography>
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        width="100%"
        gap={1}
        mt={2}
      >
        {' '}
        <Pagination
          count={Math.max(1, Math.ceil(count / ROWS_PER_PAGE))}
          page={page + 1}
          onChange={(_, p) => setPage(p - 1)}
          shape="rounded"
          sx={{
            '& .Mui-selected': {
              backgroundColor: 'var(--green-primary) !important',
              color: 'var(--white)',
            },
          }}
        />
      </Box>

      {selectedPatient && (
        <ModalFamiliar
          open={Boolean(selectedPatient)}
          // Reload on close: the relative count is captured at render time.
          onClose={() => {
            setSelectedPatient(null)
            setRefreshTrigger((p) => p + 1)
          }}
          patientID={selectedPatient.id}
          patientName={`${selectedPatient.firstName} ${selectedPatient.lastName}`}
        />
      )}
    </PageLayout>
  )
}
